import { ChatSidebar, ChatWindow } from "@/components/chat";
import { usePage } from "@/components/page/PageContext";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useChatSessions, UIContext } from "@/queries/chat";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { PanelLeftOpen } from "lucide-react";

const STORAGE_KEY = "ai.activeChatSessionId";

// ---------------------------------------------------------------------------
// ChatPage — full-page chat with sidebar + ChatWindow
// ---------------------------------------------------------------------------

export function ChatPage() {
  const { setTitles } = usePage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: sessions } = useChatSessions();

  // URL handoff from compact view (one-time).
  const sessionParam = searchParams.get("session");
  const sessionFromUrl =
    sessionParam && !isNaN(Number(sessionParam)) ? Number(sessionParam) : null;

  const [currentSessionId, setCurrentSessionId] = useState<number | null>(
    () => sessionFromUrl ?? getStoredSessionId()
  );

  // When true, user explicitly clicked "New Chat" — suppress auto-resume.
  const [isNewChatMode, setIsNewChatMode] = useState(false);

  // Guards against the session list not yet including a just-created session.
  const justCreatedIdRef = useRef<number | null>(null);

  // Read the last known retail UI context from sessionStorage (written by RetailLayout
  // whenever the user changes pages/filters). This ensures that when a user navigates
  // from e.g. "/store/3410" to "/chat", the AI still answers for that store.
  // We read once on mount — intentionally NOT live-updated from the URL — so the
  // user is anchored to the context they came from, not the current blank /chat URL.
  const [inheritedUiContext] = useState<UIContext | undefined>(() =>
    getStoredUiContext()
  );

  // -- Effects ----------------------------------------------------------

  useEffect(() => {
    setTitles(["RaidoHub Intelligence"]);
  }, [setTitles]);

  // Consume one-time URL session param to keep /chat URL clean.
  useEffect(() => {
    if (searchParams.has("session")) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Persist active session to sessionStorage (shared with compact view).
  useEffect(() => {
    if (currentSessionId) {
      sessionStorage.setItem(STORAGE_KEY, String(currentSessionId));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [currentSessionId]);

  // Validate currentSessionId against session list + auto-resume.
  useEffect(() => {
    if (!sessions) return;

    if (currentSessionId !== null) {
      const exists = sessions.some((s) => s.id === currentSessionId);
      if (exists) {
        // Session confirmed in list; clear pending marker.
        if (justCreatedIdRef.current === currentSessionId) {
          justCreatedIdRef.current = null;
        }
        return;
      }
      // Wait for the list to include a just-created session.
      if (justCreatedIdRef.current === currentSessionId) return;
      // Session no longer exists — clear.
      setCurrentSessionId(null);
      return;
    }

    // currentSessionId is null — auto-resume unless user wants a blank chat.
    if (isNewChatMode) return;
    const latestId = sessions[0]?.id ?? null;
    if (latestId) setCurrentSessionId(latestId);
  }, [sessions, currentSessionId, isNewChatMode]);

  // -- Handlers ---------------------------------------------------------

  const handleNewChat = () => {
    justCreatedIdRef.current = null;
    setIsNewChatMode(true);
    setCurrentSessionId(null);
  };

  const handleSelectSession = (sessionId: number | null) => {
    justCreatedIdRef.current = null;
    setIsNewChatMode(false);
    setCurrentSessionId(sessionId);
  };

  const handleSessionCreated = (sessionId: number) => {
    justCreatedIdRef.current = sessionId;
    setIsNewChatMode(false);
    setCurrentSessionId(sessionId);
  };

  // -- Mobile sidebar state ---------------------------------------------
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSelectSessionMobile = (sessionId: number | null) => {
    handleSelectSession(sessionId);
    setSidebarOpen(false);
  };

  // -- Render -----------------------------------------------------------

  return (
    <div className="flex h-[calc(100vh-6.5rem)] overflow-hidden rounded-xl border border-border/50 shadow-sm bg-background">
      {isMobile ? (
        <>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-80 p-0 [&>button]:hidden">
              <ChatSidebar
                currentSessionId={currentSessionId}
                onSelectSession={handleSelectSessionMobile}
                onNewChat={() => {
                  handleNewChat();
                  setSidebarOpen(false);
                }}
              />
            </SheetContent>
          </Sheet>
          <div className="flex flex-1 flex-col min-w-0">
            <div className="flex items-center border-b border-border/40 px-2 py-1.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSidebarOpen(true)}
                title="Open conversations"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </Button>
              <span className="ml-2 text-sm font-medium text-muted-foreground">
                Conversations
              </span>
            </div>
            <ChatWindow
              sessionId={currentSessionId}
              onSessionCreated={handleSessionCreated}
              uiContext={inheritedUiContext}
            />
          </div>
        </>
      ) : (
        <>
          <ChatSidebar
            currentSessionId={currentSessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
          />
          <ChatWindow
            sessionId={currentSessionId}
            onSessionCreated={handleSessionCreated}
            uiContext={inheritedUiContext}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStoredSessionId(): number | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  const num = raw ? Number(raw) : null;
  return num && !isNaN(num) ? num : null;
}

/**
 * Read the retail UI context saved by RetailLayout.
 * Returns undefined if no context has been saved (e.g. user lands directly on /chat).
 */
function getStoredUiContext(): UIContext | undefined {
  try {
    const raw = sessionStorage.getItem("ai.lastContext");
    return raw ? (JSON.parse(raw) as UIContext) : undefined;
  } catch {
    return undefined;
  }
}

export default ChatPage;
